import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { toast } from "sonner";
import { Bell, Lock, User, Copy, Check } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

export default function SetupPage({ onSetupComplete }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [totpSecret, setTotpSecret] = useState("");
  const [provisioningUri, setProvisioningUri] = useState("");
  const [copied, setCopied] = useState(false);

  const handleSetup = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error("Passwörter stimmen nicht überein");
      return;
    }
    
    if (password.length < 8) {
      toast.error("Passwort muss mindestens 8 Zeichen haben");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/setup`, { username, password });
      setTotpSecret(response.data.totp_secret);
      setProvisioningUri(response.data.provisioning_uri);
      setStep(2);
      toast.success("Admin-Benutzer erstellt");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Setup fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  const handleCopySecret = () => {
    navigator.clipboard.writeText(totpSecret);
    setCopied(true);
    toast.success("Secret kopiert");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleFinish = () => {
    onSetupComplete();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
      <div 
        className="fixed inset-0 bg-cover bg-center opacity-20"
        style={{ backgroundImage: "url('https://images.pexels.com/photos/3115127/pexels-photo-3115127.jpeg')" }}
      />
      <div className="fixed inset-0 bg-black/80" />

      <Card className="w-full max-w-md bg-[#0A0A0A] border-[#262626] relative z-10" data-testid="setup-card">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-16 h-16 bg-[#3B82F6]/10 rounded-full flex items-center justify-center mb-4">
            <Bell className="w-8 h-8 text-[#3B82F6]" />
          </div>
          <CardTitle className="text-2xl font-bold text-white font-mono">
            {step === 1 ? "Setup" : "2FA Einrichten"}
          </CardTitle>
          <CardDescription className="text-[#737373]">
            {step === 1 ? "Erstelle deinen Admin-Account" : "Scanne den QR-Code mit deiner Authenticator App"}
          </CardDescription>
        </CardHeader>

        <CardContent>
          {step === 1 ? (
            <form onSubmit={handleSetup} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username" className="text-[#737373]">Benutzername</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737373]" />
                  <Input
                    id="username"
                    data-testid="setup-username-input"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="pl-10 bg-[#171717] border-[#262626] text-white placeholder:text-[#737373] font-mono"
                    placeholder="admin"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#737373]">Passwort</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737373]" />
                  <Input
                    id="password"
                    type="password"
                    data-testid="setup-password-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 bg-[#171717] border-[#262626] text-white placeholder:text-[#737373]"
                    placeholder="••••••••"
                    required
                    minLength={8}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-[#737373]">Passwort bestätigen</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737373]" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    data-testid="setup-confirm-password-input"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 bg-[#171717] border-[#262626] text-white placeholder:text-[#737373]"
                    placeholder="••••••••"
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                data-testid="setup-submit-btn"
                disabled={loading}
                className="w-full h-12 bg-[#3B82F6] text-black hover:bg-[#3B82F6]/90 font-mono font-bold btn-primary"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                ) : (
                  "Account erstellen"
                )}
              </Button>
            </form>
          ) : (
            <div className="space-y-6">
              <div className="bg-white p-4 rounded-lg mx-auto w-fit">
                <QRCodeSVG
                  value={provisioningUri}
                  size={200}
                  level="H"
                  data-testid="qr-code"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-[#737373]">Oder gib das Secret manuell ein:</Label>
                <div className="flex gap-2">
                  <Input
                    value={totpSecret}
                    readOnly
                    className="bg-[#171717] border-[#262626] text-white font-mono text-sm"
                    data-testid="totp-secret-input"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleCopySecret}
                    className="border-[#262626] hover:bg-[#262626]"
                    data-testid="copy-secret-btn"
                  >
                    {copied ? <Check className="w-4 h-4 text-[#3B82F6]" /> : <Copy className="w-4 h-4 text-white" />}
                  </Button>
                </div>
              </div>

              <div className="bg-[#171717] p-3 rounded-lg">
                <p className="text-[#737373] text-sm">
                  <strong className="text-[#3B82F6]">Wichtig:</strong> Speichere das Secret an einem sicheren Ort. Du brauchst es, falls du die Authenticator App verlierst.
                </p>
              </div>

              <Button
                onClick={handleFinish}
                className="w-full bg-[#3B82F6] text-black hover:bg-[#3B82F6]/90 font-mono font-bold btn-primary"
                data-testid="finish-setup-btn"
              >
                Fertig - Zum Login
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
